import React, { useEffect, useState } from 'react';
import VocabFlashcard from './VocabFlashcard';
import TrueFalseGame from './TrueFalseGame';
import TopicMCQGame from './TopicMCQGame';
import ClozeGame from './ClozeGame';
import TypingGame from './TypingGame';
import GrammarRuleCard from './GrammarRuleCard';
import FillBlankGame from './FillBlankGame';
import SentenceBuilderGame from './SentenceBuilderGame';
// New vocab ladder (4 engines × 7 game_types)
import GachaGame from './GachaGame';
import MonsterTapGame from './MonsterTapGame';
import TrainSpellGame from './TrainSpellGame';
import TrainClozeGame from './TrainClozeGame';
import SpaceDefenderGame from './SpaceDefenderGame';

/**
 * Dispatcher for topic-level games. Picks renderer by `payload.ui_kind`.
 *
 * Common props passed to every component:
 *   payload: full game payload (from backend)
 *   onAnswer({ is_correct, user_answer, time_ms }): callback when user submits
 *   disabled: boolean — locks input after answer
 *
 * Recognised ui_kind values:
 *   - flashcard         (vocab tier 1)
 *   - true_false        (vocab tier 2, grammar tier 2)
 *   - mcq               (vocab tier 3 image/def-to-word/word-to-vi, grammar tier 3)
 *   - cloze             (vocab tier 3 cloze)
 *   - typing            (vocab tier 4)
 *   - grammar_rule      (grammar tier 1)
 *   - fill_blank        (grammar tier 4)
 *   - sentence_builder  (vocab tier 5, grammar tier 5)
 */
const MAP = {
  // Legacy (kept for back-compat — existing Game rows still render)
  flashcard: VocabFlashcard,
  true_false: TrueFalseGame,
  mcq: TopicMCQGame,
  cloze: ClozeGame,
  typing: TypingGame,
  grammar_rule: GrammarRuleCard,
  fill_blank: FillBlankGame,
  sentence_builder: SentenceBuilderGame,
  // New vocab ladder
  gacha: GachaGame,
  monster_tap: MonsterTapGame,
  train_spell: TrainSpellGame,
  train_cloze: TrainClozeGame,
  defender: SpaceDefenderGame,
};

export default function TopicGameRenderer({ payload, onAnswer, disabled }) {
  // Global ghost-tap shield: each question mounts a fresh renderer (keyed by
  // the caller), often right under the finger that answered the previous one
  // ("Câu tiếp" advances synchronously). Swallow ALL pointer events for the
  // first 500ms so a double-tap / delayed iOS click can't answer or skip the
  // new game (one-tap games like train-cloze and monster were skippable).
  const [inputReady, setInputReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setInputReady(true), 500);
    return () => clearTimeout(t);
  }, []);

  const Comp = MAP[payload?.ui_kind];
  if (!Comp) {
    return (
      <div style={{ padding: 16, fontFamily: 'monospace', color: '#a00' }}>
        Unsupported ui_kind: {String(payload?.ui_kind)}
        <pre style={{ marginTop: 8, fontSize: 11 }}>
          {JSON.stringify(payload, null, 2)}
        </pre>
      </div>
    );
  }
  return (
    <div style={inputReady ? undefined : { pointerEvents: 'none' }}>
      <Comp payload={payload} onAnswer={onAnswer} disabled={disabled} />
    </div>
  );
}
